import type { ICannyChangelog } from "@aix/vidmax-shared/src/constants/prismaTypings"
import { atom } from "jotai"
import { atomWithStorage } from "jotai/utils"

export const superuserPanelOpenAtom = atom(false)
export const videoTutorialAtom = atom(false)
export const changelogItemsAtom = atom([] as ICannyChangelog[])

// Dev banner dismissal with 30-day expiry
const DEV_BANNER_DISMISS_KEY = "vidmax-dev-banner-dismissed-at"
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

const getInitialDevBannerState = (): boolean => {
  if (typeof window === "undefined") return true
  const dismissedAt = localStorage.getItem(DEV_BANNER_DISMISS_KEY)
  if (!dismissedAt) return true
  const dismissedTime = parseInt(dismissedAt, 10)
  const now = Date.now()
  // Show banner again if 30 days have passed
  if (now - dismissedTime > THIRTY_DAYS_MS) {
    localStorage.removeItem(DEV_BANNER_DISMISS_KEY)
    return true
  }
  return false
}

// Base atom for the banner state
const showDevBannerBaseAtom = atom(getInitialDevBannerState())

// Writable atom that persists dismissal timestamp
export const showDevBannerAtom = atom(
  (get) => get(showDevBannerBaseAtom),
  (_get, set, newValue: boolean) => {
    set(showDevBannerBaseAtom, newValue)
    if (!newValue) {
      // User dismissed - save timestamp
      localStorage.setItem(DEV_BANNER_DISMISS_KEY, Date.now().toString())
    }
  },
)

export const showPaymentDueBannerAtom = atom(true)
export const showMaintenanceBannerAtom = atom(false)
export const secondaryNavAtom = atom(false)

// /create/ugc — remember the last avatar the user generated with so returning users land
// with it pre-selected (voice auto-pairs from the avatar) and can just type + Generate.
// Null until the first selection; the compose bar falls back to a default avatar.
export const lastUsedUgcAvatarIdAtom = atomWithStorage<string | null>("vm_ugc_last_avatar_id", null)

// Demo mode — hides all admin UI for clean screen recordings (Cmd+Shift+D)
export const demoModeAtom = atomWithStorage("vidmax-demo-mode", false)

// Two-tier navigation atoms
export const navStyleAtom = atomWithStorage<"classic" | "twoTier">("vidmax-nav-style", "twoTier")
export const activeTier1Atom = atom<string | null>(null)
export const tier2OpenAtom = atom<boolean>(false)
export const mobileTier2LevelAtom = atom<"tier1" | "tier2">("tier1")
